import { debounce } from '@dfinity/utils'
import Decimal from 'decimal.js'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { STAKE_COMMISSION } from '../../constants/business'
import { UseStakeButtonProps } from '../../interfaces/staking'
import { SwapProviderQuote } from '../../interfaces/swapInterface'
import { formatNumber } from '../../utils/formatNumber'
import { e8sToDecimals } from '../../utils/validationHelper'
import useBalances from '../useBalances'
import useSwap from '../useSwap'

export const useUnstakeButton = ({
  nicpToken,
  icpToken,
  waterNeuron,
  wnInfo,
  amount,
  commission,
  review,
}: UseStakeButtonProps) => {
  const { findBalance } = useBalances()
  const { t } = useTranslation()

  const { decimals, fee } = nicpToken

  const { performSwap, getQuotes } = useSwap({
    fromTokenId: nicpToken.id,
    toTokenId: icpToken.id,
    fromValue: e8sToDecimals(amount + commission, decimals).toString(),
  })

  const [unstakeMode, setUnstakeMode] = useState<'immediate' | 'delayed'>('delayed')
  const [quote, setQuote] = useState<SwapProviderQuote>()
  const [prompt, setPrompt] = useState<{
    title: string
    description: string
  } | null>(null)

  const debouncedGetQuotes = useMemo(
    () =>
      debounce(async (amount: bigint, commission: bigint) => {
        if (amount) {
          const normalizedAmount = e8sToDecimals(amount + commission, decimals)
          const quotes = await getQuotes(normalizedAmount.toString())
          const bestQuote = quotes
            .filter((q): q is SwapProviderQuote => !('error' in q))
            .sort((a, b) => b.price - a.price)[0]

          setQuote(bestQuote)
        } else {
          setQuote(undefined)
        }
      }, 500),
    [getQuotes, decimals]
  )

  useEffect(() => debouncedGetQuotes(amount, commission), [amount, commission])

  const balance = findBalance(nicpToken)?.balance || 0n
  const exchangeRate = wnInfo.exchange_rate
  const minWithdraw = BigInt(
    Decimal(wnInfo.minimum_withdraw_amount * exchangeRate)
      .div(Decimal(10).pow(decimals))
      .toString()
  )

  let userError
  if (!amount) {
    userError = t('stake.noAmount')
  } else if (amount + commission + fee * 2n > balance) {
    userError = t('stake.notEnough', { tokenSymbol: 'nICP' })
  } else if (!unstakeMode) {
    userError = t('stake.chooseUnstakeMode')
  } else if (unstakeMode === 'delayed' && amount < minWithdraw) {
    userError = t('stake.withdrawTooSmall', {
      minWithdraw: formatNumber(
        (Number(minWithdraw) / (1 - STAKE_COMMISSION)) * 10 ** -icpToken.decimals,
        '',
        { maximumFractionDigits: 6 }
      ),
    })
  } else if (unstakeMode === 'immediate' && !quote) {
    userError = t('stake.noSwapQuotesAvailable')
  }

  const swapNicp = async () => {
    if (quote) {
      await performSwap(quote)
    } else {
      setPrompt({
        title: t('stake.unstake.swapUnavailable'),
        description: t('stake.unstake.noSwapOffers'),
      })
    }
  }

  const unstakeNicp = async () => {
    await waterNeuron.unstake(amount, nicpToken)
  }

  const reviewStake = () => {
    let toAmount
    if (unstakeMode === 'delayed') {
      toAmount = amount / exchangeRate
    } else {
      toAmount = quote?.priceHuman || 0
    }

    const attrs = {
      [t('stake.review.unstakeMode')]: t(`stake.unstake.${unstakeMode}.title`),
    }

    if (unstakeMode === 'immediate') {
      attrs[t('stake.review.swapProvider')] = quote?.provider || ''
    }

    review({
      toAmount: formatNumber(Number(toAmount))!,
      attrs,
      action: async () => {
        if (unstakeMode === 'immediate') {
          await swapNicp()
        } else {
          await unstakeNicp()
        }
      },
    })
  }

  const onInfoClick = (event: any, stakeMode: 'immediate' | 'delayed') => {
    setPrompt({
      title: t(`stake.unstake.${stakeMode}.title`),
      description: t(`stake.unstake.${stakeMode}.description`),
    })
  }

  return {
    setUnstakeMode,
    unstakeMode,
    onInfoClick,
    userError,
    reviewStake,
    setPrompt,
    prompt,
    delayedAmount: amount ? formatNumber(Number(amount / exchangeRate), 'ICP') : '-/-',
    immediateAmount: quote ? formatNumber(quote.priceHuman, 'ICP') : '-/-',
  }
}
